import { useEffect, useRef, type RefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { useMapActions, useMapSelector } from "../store/map";
import { isBboxContained } from "../utilities/bboxUtils";

/**
 * useCmTableBbox
 *
 * Listens to the MapLibre map's "moveend" event and writes a debounced
 * viewport bounding-box string "minLng,minLat,maxLng,maxLat" into the
 * global map store (state.cmTableBbox).
 *
 * Optimization: Only sets `isMapBboxDebouncing` to true if the new viewport
 * is NOT contained within the last committed bbox (meaning an API fetch is likely).
 *
 * Must be called inside MapComponent (which owns the mapRef).
 *
 * Fix (C3): The moveend listener is attached ONCE for the component lifetime.
 * The last-committed bbox is tracked in a ref so onMoveEnd always reads the
 * latest value without lastCommittedBboxStr being a dep — which previously
 * caused the listener to tear down and re-attach on every bbox commit.
 */
export const useCmTableBbox = (mapRef: RefObject<MapRef | null>, debounceMs = 500, suppressRef?: RefObject<boolean>): void => {
  const { setCmTableBbox, setIsMapBboxDebouncing } = useMapActions();
  const { cmTableBbox: lastCommittedBboxStr } = useMapSelector();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest committed bbox in a ref so onMoveEnd can read it
  // without it being listed as an effect dependency.
  const lastCommittedBboxRef = useRef<string | null>(lastCommittedBboxStr);
  lastCommittedBboxRef.current = lastCommittedBboxStr;

  useEffect(() => {
    let attached = false;
    let map: ReturnType<MapRef["getMap"]> | null = null;

    const captureBbox = () => {
      if (!map) return;
      const bounds = map.getBounds();
      if (!bounds) return;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const bboxStr = [sw.lng.toFixed(6), sw.lat.toFixed(6), ne.lng.toFixed(6), ne.lat.toFixed(6)].join(",");

      setCmTableBbox(bboxStr);
      setIsMapBboxDebouncing(false);
    };

    const onMoveEnd = () => {
      if (!map) return;

      // Ignore programmatic popup-clamp pans (ensureGeoPopupInViewport). These are tiny
      // camera nudges to keep a popup visible — they neither need a refetch nor should they
      // flash the "Processing Layer" indicator.
      if (suppressRef?.current) return;

      // Determine if the new viewport is outside the last committed area.
      // Read from ref — stable reference, no re-attach needed.
      let needsFetch = true;
      const lastBboxStr = lastCommittedBboxRef.current;
      if (lastBboxStr) {
        const lastBbox = lastBboxStr.split(",").map(Number);
        const bounds = map.getBounds();
        if (bounds) {
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const currentBbox = [sw.lng, sw.lat, ne.lng, ne.lat];

          if (isBboxContained(lastBbox, currentBbox)) {
            needsFetch = false;
          }
        }
      }

      // Always signal debouncing so the sidebar can show a loading indicator
      // while the sort is pending. (needsFetch is still used for API-fetch gating elsewhere.)
      setIsMapBboxDebouncing(true);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(captureBbox, debounceMs);
    };

    const tryAttach = () => {
      const m = mapRef.current?.getMap();
      if (!m || attached) return;
      map = m;
      attached = true;

      if (m.isStyleLoaded()) {
        captureBbox();
      } else {
        m.once("load", captureBbox);
      }

      m.on("moveend", onMoveEnd);
    };

    tryAttach();
    const interval = setInterval(() => {
      if (!attached) tryAttach();
      else clearInterval(interval);
    }, 200);

    return () => {
      clearInterval(interval);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (map && attached) {
        map.off("moveend", onMoveEnd);
      }
    };
    // Stable deps only — lastCommittedBboxStr is consumed via ref above.
  }, [mapRef, debounceMs, setCmTableBbox, setIsMapBboxDebouncing, suppressRef]);
};
